import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  EMPTY,
  endWith,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';

import { AuthService } from '../../../../core/services/auth.service';
import { APP_PATHS } from '../../../../shared/constants/routes.constants';
import { RESET_PASSWORD_CONFIG } from '../reset-password.config';
import { ResetPasswordActions, ResetPasswordApiActions } from './reset-password.actions';
import { mapUpdatePasswordError, mapVerifyTokenError } from './reset-password.error-mapping';
import {
  selectResendAttempts,
  selectResetToken,
  selectRetry,
  selectSubmittedEmail,
} from './reset-password.selectors';

@Injectable()
export class ResetPasswordEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  public verifyToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.enterPage),
      switchMap(({ token }) => {
        if (!token) {
          return EMPTY;
        }

        return this.authService.verifyResetToken(token).pipe(
          map(() => ResetPasswordApiActions.verifyTokenSuccess()),
          catchError((error: HttpErrorResponse) =>
            of(ResetPasswordApiActions.verifyTokenFailure({ error: mapVerifyTokenError(error) })),
          ),
        );
      }),
    ),
  );

  public requestEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.submitEmailRequest),
      switchMap(({ email }) =>
        this.authService.requestPasswordReset(email).pipe(
          map(() => ResetPasswordApiActions.requestEmailSuccess()),
          catchError(() => of(ResetPasswordApiActions.requestEmailFailure({ error: 'NETWORK' }))),
        ),
      ),
    ),
  );

  public resendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.resendEmail),
      withLatestFrom(this.store.select(selectSubmittedEmail)),
      switchMap(([, email]) =>
        this.authService.requestPasswordReset(email).pipe(
          map(() => ResetPasswordApiActions.resendEmailSuccess()),
          catchError(() => of(ResetPasswordApiActions.resendEmailFailure({ error: 'NETWORK' }))),
        ),
      ),
    ),
  );

  public updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.submitNewPassword),
      withLatestFrom(this.store.select(selectResetToken)),
      switchMap(([{ newPassword }, token]) => {
        if (!token) {
          return of(ResetPasswordApiActions.updatePasswordFailure({ error: 'EXPIRED_TOKEN' }));
        }

        return this.authService.resetPassword(token, newPassword).pipe(
          map(() => ResetPasswordApiActions.updatePasswordSuccess()),
          catchError((error: HttpErrorResponse) =>
            of(
              ResetPasswordApiActions.updatePasswordFailure({
                error: mapUpdatePasswordError(error),
              }),
            ),
          ),
        );
      }),
    ),
  );

  public validateNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.newPasswordChanged),
      debounceTime(RESET_PASSWORD_CONFIG.NEW_PASSWORD_CHECK_DEBOUNCE_MS),
      withLatestFrom(this.store.select(selectResetToken)),
      switchMap(([{ newPassword }, token]) => {
        if (!newPassword || !token) {
          return of(ResetPasswordApiActions.validateNewPasswordSuccess({ matchesCurrent: false }));
        }

        return this.authService.validateNewPassword(token, newPassword).pipe(
          map((matchesCurrent) =>
            ResetPasswordApiActions.validateNewPasswordSuccess({ matchesCurrent }),
          ),
          catchError(() => of(ResetPasswordApiActions.validateNewPasswordFailure())),
          takeUntil(this.actions$.pipe(ofType(ResetPasswordActions.newPasswordCheckReset))),
        );
      }),
    ),
  );

  public retry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.retry),
      withLatestFrom(this.store.select(selectRetry)),
      mergeMap(([, retry]) => {
        if (!retry || !this.isOnline()) {
          return EMPTY;
        }

        switch (retry.type) {
          case 'requestEmail':
            return of(ResetPasswordActions.submitEmailRequest({ email: retry.email }));
          case 'resendEmail':
            return of(ResetPasswordActions.resendEmail());
          case 'verifyToken':
            return of(ResetPasswordActions.enterPage({ token: retry.token }));
          case 'updatePassword':
            return of(ResetPasswordActions.submitNewPassword({ newPassword: retry.newPassword }));
        }
      }),
    ),
  );

  public startCooldown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordApiActions.resendEmailSuccess),
      withLatestFrom(this.store.select(selectResendAttempts)),
      map(([, attempts]) => {
        const seconds =
          attempts >= RESET_PASSWORD_CONFIG.RESEND_LONG_COOLDOWN_ATTEMPT
            ? RESET_PASSWORD_CONFIG.RESEND_LONG_COOLDOWN_SECONDS
            : RESET_PASSWORD_CONFIG.RESEND_COOLDOWN_SECONDS;

        return ResetPasswordActions.resendCooldownStarted({ seconds });
      }),
    ),
  );

  public cooldownTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordActions.resendCooldownStarted),
      switchMap(({ seconds }) =>
        timer(1000, 1000).pipe(
          take(seconds),
          map((tick) => ResetPasswordActions.resendCooldownTick({ remaining: seconds - tick - 1 })),
          endWith(ResetPasswordActions.resendCooldownFinished()),
          takeUntil(
            this.actions$.pipe(
              ofType(
                ResetPasswordActions.leavePage,
                ResetPasswordActions.startNewRequest,
                ResetPasswordActions.enterPage,
              ),
            ),
          ),
        ),
      ),
    ),
  );

  public navigateOnStartNewRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ResetPasswordActions.startNewRequest),
        tap(() => {
          this.router.navigate(['/', APP_PATHS.AUTH, APP_PATHS.RESET_PASSWORD]);
        }),
      ),
    { dispatch: false },
  );

  private isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine;
  }
}
